import { SearchRegistry, SearchRegistryEntry } from './types';
import { accountManagementSearchEntries } from './sections/account-management';
import { backofficeSearchEntries } from './sections/backoffice';
import { bmwSearchEntries } from './sections/bmw';
import { customerSupportSearchEntries } from './sections/customer-support';
import { dashboardSearchEntries } from './sections/dashboard';
import { developerHubSearchEntries } from './sections/internal-developer-hub';
import { fleetManagementSearchEntries } from './sections/fleet-management';
import { individualPartnersSearchEntries } from './sections/individual-partners';
import { insightsAnalyticsSearchEntries } from './sections/insights-analytics';
import { integrationAndSettingsSearchEntries } from './sections/integration-and-settings';
import { integrationGuideSearchEntries } from './sections/integration-guide';
import { partnerAdministrationSearchEntries } from './sections/partner-administration';
import { platformStatusSearchEntries } from './sections/platform-status';
import { rydFinancialSearchEntries } from './sections/ryd-financial';
import { rydFleetOperationsSearchEntries } from './sections/ryd-fleet-operations';
import { rydMarketingSearchEntries } from './sections/ryd-marketing';
import { settingsAndSupportSearchEntries } from './sections/settings-and-support';
import { troubleshootingSearchEntries } from './sections/troubleshooting';
import { userManagementSearchEntries } from './sections/user-management';
import { webhookManagementSearchEntries } from './sections/webhook-management';
import { aiAtRydSearchEntries } from './sections/ai-at-ryd';
import { securityAndComplianceSearchEntries } from './sections/security-and-compliance';

/**
 * Central registry of search metadata (keywords, questions, FAQs) for portal
 * pages, keyed by exact route path. Add a new section file under
 * `./sections/` and merge it here when extending the registry.
 *
 * Section files mirror `src/constants/menu/sections/` so authors edit the
 * menu and registry side by side.
 *
 * Consumed by the global header search (`src/components/header/search.vue`).
 * Phase 1 uses it for substring matching and the FAQ matches results
 * section. Phase 2 (AI Q&A) will re-index the same data into the docs
 * corpus — see `concepts-01-doc-discovery.md` §8.
 */
export const searchRegistry: SearchRegistry = {
  '/pay-operations/receipt-upload': {
    keywords: [
      'receipt',
      'upload',
      'receipt upload',
      'NFC',
      'allnet',
      'force upload',
      'receipt image',
      'expired receipt',
      'Beleg',
      'Kassenbon',
      'adjust image',
    ],
    questions: ['How can I upload a receipt for an expired NFC order?'],
    faqs: [
      {
        q: 'Does uploading a receipt only test the image?',
        a: 'No. Every upload changes the real order in the backend environment. Sent means queued for extraction; check the recorded outcome before uploading again.',
      },
    ],
    urlParameters: ['orderUuid'],
  },
  ...accountManagementSearchEntries,
  ...backofficeSearchEntries,
  ...bmwSearchEntries,
  ...customerSupportSearchEntries,
  ...dashboardSearchEntries,
  ...developerHubSearchEntries,
  ...fleetManagementSearchEntries,
  ...individualPartnersSearchEntries,
  ...insightsAnalyticsSearchEntries,
  ...integrationAndSettingsSearchEntries,
  ...integrationGuideSearchEntries,
  ...partnerAdministrationSearchEntries,
  ...platformStatusSearchEntries,
  ...rydFinancialSearchEntries,
  ...rydFleetOperationsSearchEntries,
  ...rydMarketingSearchEntries,
  ...settingsAndSupportSearchEntries,
  ...troubleshootingSearchEntries,
  ...userManagementSearchEntries,
  ...webhookManagementSearchEntries,
  ...aiAtRydSearchEntries,
  ...securityAndComplianceSearchEntries,
};

export function getSearchRegistryEntry(path: string | undefined): SearchRegistryEntry | undefined {
  if (!path) return undefined;
  return searchRegistry[path];
}

export type { SearchRegistry, SearchRegistryEntry, SearchFaq } from './types';
